import React, { useState, useEffect } from 'react';
import { useSocket } from './hooks/useSocket';
import Header from './components/Header';
import JoinScreen from './components/JoinScreen';
import WaitingScreen from './components/WaitingScreen';
import SelectStatScreen from './components/SelectStatScreen';
import WaitingStatScreen from './components/WaitingStatScreen';
import SelectPlayerScreen from './components/SelectPlayerScreen';
import WaitingOthersScreen from './components/WaitingOthersScreen';
import RoundResultsScreen from './components/RoundResultsScreen';
import GameFinishedScreen from './components/GameFinishedScreen';
import ArrangeScreen from './components/ArrangeScreen';
import GuessScreen from './components/GuessScreen';
import ArrangeResults from './components/ArrangeResults';
import GuessResults from './components/GuessResults';
import GuessGameFinishedScreen from './components/GuessGameFinishedScreen';
import LiveKitProvider from './video/LiveKitProvider';
import './styles.css';

const GUESS_SPOT_SCREENS = new Set(['waiting', 'guess', 'guessResults', 'guessGameFinished']);

const App = () => {
  const socket = useSocket();
  const [gameState, setGameState] = useState({
    currentScreen: 'join',
    playerId: null,
    gameId: null,
    gameMode: 'trump-card',
    playerCount: 0,
    players: [],
    roundInfo: '',
    error: null,
    screenData: null,
    guessRoundHistory: [],
    socketConnected: false,
    isHost: false
  });

  useEffect(() => {
    console.log('App useEffect triggered, socket:', socket);
    if (!socket) {
      console.log('No socket in App useEffect - will wait for socket');
      return;
    }
    
    console.log('Setting up socket event listeners in App');

    // Check if socket is already connected
    if (socket.connected) {
      console.log('Socket already connected in App component');
      setGameState(prev => ({ ...prev, socketConnected: true, error: null }));
    }

    // Socket event listeners
    socket.on('connect', () => {
      console.log('Socket connected successfully in App component');
      setGameState(prev => ({ ...prev, socketConnected: true, error: null }));
    });
    
    socket.on('joined_game', (data) => {
      console.log('Received joined_game event', data);
      setGameState(prev => ({
        ...prev,
        gameId: data.gameId,
        players: data.players,
        playerCount: data.playerCount,
        currentScreen: 'waiting',
        isHost: data.isHost ?? data.playerCount === 1,
        error: null
      }));
    });

    socket.on('player_joined', (data) => {
      setGameState(prev => ({
        ...prev,
        players: [...prev.players, data.playerId],
        playerCount: data.playerCount
      }));
    });

    socket.on('player_left', (data) => {
      setGameState(prev => ({
        ...prev,
        players: data.players ?? prev.players.filter(p => p !== data.playerId),
        playerCount: data.playerCount,
        // Server transfers host when the host leaves
        isHost: data.hostPlayer ? prev.playerId === data.hostPlayer : prev.isHost
      }));
    });

    socket.on('rematch_started', (data) => {
      setGameState(prev => ({
        ...prev,
        currentScreen: 'waiting',
        players: data.players,
        playerCount: data.playerCount,
        isHost: prev.playerId === data.hostPlayer,
        screenData: null,
        guessRoundHistory: [],
        roundInfo: '',
        error: null
      }));
    });

    socket.on('join_error', (data) => {
      setGameState(prev => ({
        ...prev,
        error: data.message
      }));
    });

    socket.on('game_expired', () => {
      setGameState(prev => ({
        ...prev,
        currentScreen: 'join',
        playerId: null,
        gameId: null,
        playerCount: 0,
        players: [],
        roundInfo: '',
        screenData: null,
        guessRoundHistory: [],
        isHost: false,
        error: 'Game expired due to inactivity.'
      }));
    });

    socket.on('game_full', () => {
      console.log('Received game_full event');
      setGameState(prev => ({
        ...prev,
        error: 'Game is full! Try a different game ID.'
      }));
    });

    socket.on('select_statistic', (data) => {
      setGameState(prev => ({
        ...prev,
        currentScreen: 'selectStat',
        screenData: data,
        error: null
      }));
    });

    socket.on('waiting_for_statistic', (data) => {
      setGameState(prev => ({
        ...prev,
        currentScreen: 'waitingStat',
        screenData: data,
        error: null
      }));
    });

    socket.on('select_player', (data) => {
      setGameState(prev => ({
        ...prev,
        currentScreen: 'selectPlayer',
        screenData: data,
        error: null
      }));
    });

    socket.on('waiting_for_others', (data) => {
      setGameState(prev => ({
        ...prev,
        currentScreen: 'waitingOthers',
        screenData: data,
        error: null
      }));
    });

    socket.on('round_result', (data) => {
      setGameState(prev => ({
        ...prev,
        currentScreen: 'results',
        screenData: data,
        error: null
      }));
    });

    socket.on('game_finished', (data) => {
      setGameState(prev => ({
        ...prev,
        currentScreen: 'gameFinished',
        screenData: data,
        error: null
      }));
    });

    // New game mode events
    socket.on('start_arrange', (data) => {
      setGameState(prev => ({
        ...prev,
        currentScreen: 'arrange',
        screenData: data,
        error: null
      }));
    });

    socket.on('start_guess', (data) => {
      setGameState(prev => ({
        ...prev,
        currentScreen: 'guess',
        screenData: data,
        error: null
      }));
    });

    socket.on('arrangement_results', (data) => {
      setGameState(prev => ({
        ...prev,
        currentScreen: 'arrangeResults',
        screenData: data,
        error: null
      }));
    });

    socket.on('guess_results', (data) => {
      setGameState(prev => ({
        ...prev,
        currentScreen: 'guessResults',
        screenData: data,
        guessRoundHistory: [
          ...prev.guessRoundHistory,
          {
            round: data.currentRound,
            results: data.results,
            cricketer: data.cricketer,
          },
        ],
        error: null,
      }));
    });

    socket.on('arrange_error', (data) => {
      setGameState(prev => ({
        ...prev,
        error: data.message
      }));
    });

    socket.on('guess_error', (data) => {
      setGameState(prev => ({
        ...prev,
        error: data.message
      }));
    });

    socket.on('disconnect', () => {
      console.log('Socket disconnected');
      setGameState(prev => ({
        ...prev,
        socketConnected: false,
        error: 'Disconnected from server. Please refresh the page.'
      }));
    });

    // Cleanup
    return () => {
      if (socket) {
        console.log('Cleaning up socket event listeners');
        socket.off('connect');
        socket.off('joined_game');
        socket.off('player_joined');
        socket.off('player_left');
        socket.off('rematch_started');
        socket.off('join_error');
        socket.off('game_expired');
        socket.off('game_full');
        socket.off('select_statistic');
        socket.off('waiting_for_statistic');
        socket.off('select_player');
        socket.off('waiting_for_others');
        socket.off('round_result');
        socket.off('game_finished');
        socket.off('start_arrange');
        socket.off('start_guess');
        socket.off('arrangement_results');
        socket.off('guess_results');
        socket.off('arrange_error');
        socket.off('guess_error');
        socket.off('disconnect');
      }
    };
  }, [socket]);

  const handleJoinGame = (playerName, gameId, gameMode) => {
    console.log('handleJoinGame called', { playerName, gameId, gameMode, socket, connected: gameState.socketConnected });
    
    if (!socket) {
      console.log('No socket connection');
      setGameState(prev => ({ ...prev, error: 'No socket connection. Please refresh the page.' }));
      return;
    }
    
    if (!gameState.socketConnected) {
      console.log('Socket not connected yet');
      setGameState(prev => ({ ...prev, error: 'Connecting to server... Please try again in a moment.' }));
      return;
    }
    
    console.log('Socket connected, emitting join_game');
    
    setGameState(prev => ({
      ...prev,
      playerId: playerName,
      gameMode: gameMode,
      error: null
    }));
    
    socket.emit('join_game', { gameId, playerId: playerName, gameMode });
  };

  const handleSelectStatistic = (statistic) => {
    if (!socket) return;
    socket.emit('select_statistic', { statistic });
  };

  const handleSelectPlayer = (player) => {
    if (!socket) return;
    socket.emit('select_player', { player });
  };

  const handleNewGame = () => {
    setGameState({
      currentScreen: 'join',
      playerId: null,
      gameId: null,
      gameMode: 'trump-card',
      playerCount: 0,
      players: [],
      roundInfo: '',
      error: null,
      screenData: null,
      guessRoundHistory: [],
      socketConnected: socket?.connected || false,
      isHost: false
    });
  };

  const handleViewFinalStandings = () => {
    setGameState(prev => ({ ...prev, currentScreen: 'guessGameFinished' }));
  };

  const handleLeaveGame = () => {
    if (socket && gameState.gameId) {
      socket.emit('leave_game');
    }
    handleNewGame();
  };

  const handlePlayAgain = () => {
    if (!socket) return;
    socket.emit('play_again');
  };

  const handleHostStartGame = () => {
    if (!socket) return;
    socket.emit('host_start_game');
  };

  const handleSubmitArrangement = (arrangement) => {
    if (!socket) return;
    socket.emit('submit_arrangement', { arrangement });
  };

  const handleSubmitGuess = (guess) => {
    if (!socket) return;
    socket.emit('submit_guess', { guess });
  };

  const renderScreen = () => {
    switch (gameState.currentScreen) {
      case 'join':
        return (
          <JoinScreen
            onJoinGame={handleJoinGame}
            error={gameState.error}
            connected={gameState.socketConnected}
          />
        );
      
      case 'waiting':
        return (
          <WaitingScreen
            players={gameState.players}
            gameMode={gameState.gameMode}
            isHost={gameState.isHost}
            gameId={gameState.gameId}
            onHostStartGame={handleHostStartGame}
            onLeave={handleLeaveGame}
          />
        );
      
      case 'selectStat':
        return (
          <SelectStatScreen
            data={gameState.screenData}
            onSelectStatistic={handleSelectStatistic}
          />
        );
      
      case 'waitingStat':
        return (
          <WaitingStatScreen
            currentPlayer={gameState.screenData?.currentPlayer}
          />
        );
      
      case 'selectPlayer':
        return (
          <SelectPlayerScreen
            data={gameState.screenData}
            onSelectPlayer={handleSelectPlayer}
          />
        );
      
      case 'waitingOthers':
        return (
          <WaitingOthersScreen
            statistic={gameState.screenData?.statistic}
          />
        );
      
      case 'results':
        return (
          <RoundResultsScreen
            data={gameState.screenData}
          />
        );
      
      case 'gameFinished':
        return (
          <GameFinishedScreen
            data={gameState.screenData}
            onPlayAgain={handlePlayAgain}
            onExitToHome={handleLeaveGame}
          />
        );

      case 'arrange':
        return (
          <ArrangeScreen
            cricketers={gameState.screenData?.cricketers || []}
            statistic={gameState.screenData?.statistic || ''}
            isHost={gameState.screenData?.isHost || false}
            currentRound={gameState.screenData?.currentRound || 1}
            totalRounds={gameState.screenData?.totalRounds || 5}
            onSubmitArrangement={handleSubmitArrangement}
          />
        );

      case 'guess':
        return (
          <GuessScreen
            onLeave={handleLeaveGame}
            cricketer={gameState.screenData?.cricketer || {}}
            xAxis={gameState.screenData?.xAxis || ''}
            yAxis={gameState.screenData?.yAxis || ''}
            xRange={gameState.screenData?.xRange || {min: 0, max: 100}}
            yRange={gameState.screenData?.yRange || {min: 0, max: 100}}
            isHost={gameState.screenData?.isHost || false}
            currentRound={gameState.screenData?.currentRound || 1}
            totalRounds={gameState.screenData?.totalRounds || 5}
            players={gameState.players}
            currentPlayerId={gameState.playerId}
            onSubmitGuess={handleSubmitGuess}
          />
        );

      case 'arrangeResults':
        return (
          <ArrangeResults
            results={gameState.screenData?.results || []}
            correctOrder={gameState.screenData?.correctOrder || []}
            statistic={gameState.screenData?.statistic || ''}
            scores={gameState.screenData?.scores || []}
            cricketers={gameState.screenData?.cricketers || []}
            currentPlayerId={gameState.playerId}
            currentRound={gameState.screenData?.currentRound || 1}
            totalRounds={gameState.screenData?.totalRounds || 5}
            isGameFinished={gameState.screenData?.isGameFinished || false}
            onPlayAgain={handlePlayAgain}
            onExitToHome={handleLeaveGame}
          />
        );

      case 'guessResults':
        return (
          <GuessResults
            results={gameState.screenData?.results || []}
            correctPosition={gameState.screenData?.correctPosition || {x: 50, y: 50}}
            cricketer={gameState.screenData?.cricketer || {}}
            xAxis={gameState.screenData?.xAxis || ''}
            yAxis={gameState.screenData?.yAxis || ''}
            xRange={gameState.screenData?.xRange || {min: 0, max: 100}}
            yRange={gameState.screenData?.yRange || {min: 0, max: 100}}
            scores={gameState.screenData?.scores || []}
            currentRound={gameState.screenData?.currentRound || 1}
            totalRounds={gameState.screenData?.totalRounds || 5}
            isGameFinished={gameState.screenData?.isGameFinished || false}
            nextRoundIn={gameState.screenData?.nextRoundIn ?? null}
            players={gameState.players}
            currentPlayerId={gameState.playerId}
            onLeave={handleLeaveGame}
            onViewFinalStandings={handleViewFinalStandings}
          />
        );

      case 'guessGameFinished':
        return (
          <GuessGameFinishedScreen
            finalScores={gameState.screenData?.scores || []}
            roundHistory={gameState.guessRoundHistory}
            totalRounds={gameState.screenData?.totalRounds || 5}
            players={gameState.players}
            currentPlayerId={gameState.playerId}
            onPlayAgain={handlePlayAgain}
            onExitToHome={handleLeaveGame}
          />
        );

      default:
        return (
          <JoinScreen
            onJoinGame={handleJoinGame}
            error={gameState.error}
            connected={gameState.socketConnected}
          />
        );
    }
  };

  const isPenScreen =
    gameState.currentScreen === 'guess' ||
    gameState.currentScreen === 'guessResults' ||
    gameState.currentScreen === 'guessGameFinished' ||
    gameState.currentScreen === 'join' ||
    gameState.currentScreen === 'waiting' ||
    gameState.currentScreen === 'gameFinished';

  useEffect(() => {
    document.body.classList.toggle('pen-active', isPenScreen);
    return () => document.body.classList.remove('pen-active');
  }, [isPenScreen]);

  const inGuessSpotRoom =
    gameState.gameMode === 'guess-spot' &&
    GUESS_SPOT_SCREENS.has(gameState.currentScreen) &&
    !!gameState.gameId &&
    !!gameState.playerId;

  const screen = renderScreen();
  const wrappedScreen = inGuessSpotRoom ? (
    <LiveKitProvider
      roomName={gameState.gameId}
      identity={gameState.playerId}
      name={gameState.playerId}
      role={gameState.isHost ? 'host' : 'guest'}
      socket={socket}
    >
      {screen}
    </LiveKitProvider>
  ) : (
    screen
  );

  return (
    <>
      {!isPenScreen && (
        <Header
          playerCount={gameState.playerCount}
          roundInfo={gameState.roundInfo}
          gameMode={gameState.gameMode}
          onLeave={gameState.currentScreen !== 'join' ? handleLeaveGame : undefined}
        />
      )}
      <main>
        <div className="desktop-frame">
          {wrappedScreen}
        </div>
      </main>
    </>
  );
};

export default App;